import React from 'react';
import classNames, { Argument } from 'classnames';
import { XMarkIcon } from '@heroicons/react/24/solid';

interface KeyboardInputProps {
  onClear: () => void;
  // The focused input's `type` (e.g. 'text', 'password', 'textarea'). The display adapts to it:
  // 'password' masks the value, 'textarea' renders a multi-line area.
  type?: string;
  className?: Argument;
  value: string | number;
  selection: [number, number];
}

const INPUT_MASK = '•';

const KeyboardInput: React.FC<KeyboardInputProps> = ({ value, selection = [0, 0], className, onClear, type }) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const isMultiline = type === 'textarea';

  const [start, end] = selection;
  const text = type === 'password' ? INPUT_MASK.repeat(value.toString()?.length):  value.toString();
  const before = text.slice(0, start);
  const highlight = text.slice(start, end);
  const after = text.slice(end);

  const hasValue = Boolean(value);

  React.useEffect(() => {
    const container = scrollRef.current;
    if (container) {
      if (isMultiline) {
        container.scrollTop = container.scrollHeight;
      } else {
        container.scrollLeft = container.scrollWidth;
      }
    }
  }, [text, isMultiline]);

  return (
    <div className="bg-input-bg p-4 flex flex-col gap-4 w-full rounded-card">
      <div className={classNames(
        'relative flex input focus:outline-hidden peer bg-input-bg pt-8 bg-white py-5! scrollbar-none',
        {
          'items-start text-left min-h-35': isMultiline,
          'items-center text-center min-h-17 overflow-x-auto': !isMultiline,
        },
        className
      )}
      >
        <div ref={scrollRef} className={classNames('grow', isMultiline ? 'max-h-[30vh] overflow-y-auto' : 'overflow-hidden')}>
          <span className={classNames(
            'text-black pr-10 will-change-transform',
            isMultiline ? 'block whitespace-pre-wrap wrap-break-word' : 'inline-block whitespace-pre',
            className,
          )}>
            {before}
            {highlight && <u>{highlight}</u>}
            {after}
            <span data-testid="keyboard-caret" className="keyboard-input-caret" />
          </span>
        </div>

        {Boolean(onClear) && hasValue && (
          <div
            onClick={onClear}
            data-testid="input-clear-btn"
            className="absolute top-4 right-8 size-7 w-16 h-full -mt-4 -mr-4 cursor-pointer flex justify-center items-center clearIcon"
          >
            <XMarkIcon className="m-auto size-3.5 w-8 h-8" />
          </div>
        )}
      </div>
    </div>
  );
};

export default KeyboardInput;
